<script lang="ts">
  import Router, { push } from "svelte-spa-router";
  import { onMount } from "svelte";
  import Login from "./routes/Login.svelte";
  import Class from "./routes/Class.svelte";
  import ForgotPassword from "./routes/ForgotPassword.svelte";
  import SetNewPassword from "./routes/SetNewPassword.svelte";
  import VerifyEmail from "./routes/VerifyEmail.svelte";
  import ConfirmEmail from "./routes/ConfirmEmail.svelte";
  import Profile from "./routes/Profile.svelte";
  import Demo from "./routes/Demo.svelte";
  import { me } from "./lib/api";
  import { setUser, setLoading, session } from "./lib/session.svelte";
  import { setLocale, DEFAULT_LOCALE, _ } from "./lib/i18n";
  import { hasPendingReturnRoom } from "./lib/e2eeRecoveryReturn";
  import InstallPrompt from "./lib/InstallPrompt.svelte";

  const routes = {
    "/": Login,
    "/class": Class,
    "/profile": Profile,
    "/reset": ForgotPassword,
    "/reset/:token": SetNewPassword,
    "/verify/:token": VerifyEmail,
    "/confirm-email/:token": ConfirmEmail,
    // Demo-only autologin (demo student). Reached from the public website.
    "/demo": Demo,
    "*": Login,
  };

  /** Hashes that should NOT be auto-redirected to /class on an active session. */
  function isUnauthRoute(): boolean {
    const h = window.location.hash;
    return (
      h.startsWith("#/reset") ||
      h.startsWith("#/verify") ||
      h.startsWith("#/confirm-email")
    );
  }

  // Skip the session bootstrap when landing on the demo route so its own
  // autologin owns the flow (avoids racing me() against demo login).
  const isDemoRoute =
    typeof window !== "undefined" && window.location.hash.startsWith("#/demo");

  onMount(async () => {
    if (isDemoRoute) {
      setLocale(DEFAULT_LOCALE);
      return;
    }
    setLoading(true);
    try {
      const res = await me();
      const language = res.language ?? DEFAULT_LOCALE;
      setUser({
        email: res.email,
        display_name: res.display_name,
        full_name: res.full_name ?? null,
        roles: res.roles,
        language,
        demo: res.demo === true,
        must_change_password: res.must_change_password === true,
      });
      setLocale(language);
      // E2EE repair bounce: if Class stashed a return room (login-time identity
      // provisioning failed and the student chose to repair), route to Login
      // instead of /class even though the session cookie is still valid — we
      // need the student to re-enter the password so the identity can be
      // re-provisioned. Login returns to /class on success and Class auto-rejoins.
      if (!isUnauthRoute() && !hasPendingReturnRoom()) push("/class");
    } catch {
      // No session — fall back to the default student locale (DE).
      setUser(null);
      setLocale(DEFAULT_LOCALE);
    } finally {
      setLoading(false);
    }
  });
</script>

<main>
  {#if $session.loading}
    <div class="boot" data-testid="boot">{$_("boot.loading")}</div>
  {/if}
  <Router {routes} />
  <InstallPrompt />
</main>

<style>
  main {
    min-height: 100%;
  }
  .boot {
    position: fixed;
    inset: 0;
    display: grid;
    place-items: center;
    background: #1f2937;
    color: #f3f4f6;
    z-index: 10;
  }
</style>
